import is from '@sindresorhus/is';
import { type FunctionComponent, useCallback, useEffect, useRef } from 'react';

import { theme } from '../../theme/cobrainer-theme';
import { TILE_WIDTH_CSS_TRANSITION } from './constants';
import { useSetTileBox, useSetTileSlot, useTileErrorBoundary, useTileWidthContext } from './TilingEngineContext';
import type { TilingEngineRightDockProps } from './types';
import { useEndReservation } from './use-end-reservation';

const COLLAPSED_WIDTH = '0px';

const STICKY_Z_INDEX = 10;

const isCollapsed = (width: string): boolean => width === '0px' || width === '0';

export const TilingEngineRightDock: FunctionComponent<TilingEngineRightDockProps> = ({
    children,
    id,
    maxWidth,
    minWidth,
    width = COLLAPSED_WIDTH,
}) => {
    const setTileSlot = useSetTileSlot();
    const setTileBox = useSetTileBox();
    const tileWidths = useTileWidthContext();
    const ErrorBoundary = useTileErrorBoundary();
    const slotRef = useRef<HTMLDivElement>(null);
    const outerRef = useRef<HTMLDivElement | null>(null);

    const widthStyle = tileWidths.get(id);
    const resolvedWidth = widthStyle?.width ?? width;
    const collapsed = isCollapsed(resolvedWidth);

    useEndReservation({ active: !collapsed, id, nodeRef: outerRef });

    const setOuterRef = useCallback(
        (node: HTMLDivElement | null) => {
            outerRef.current = node;
            setTileBox(id, node);
        },
        [id, setTileBox],
    );

    useEffect(() => {
        setTileSlot(id, slotRef.current);

        return () => {
            setTileSlot(id, null);
        };
    }, [id, setTileSlot]);

    // Cancel flex gap-m so collapsed dock takes zero space.
    const gapCancellingMarginLeft = collapsed ? `calc(-1 * ${theme.spacing.m})` : '0px';

    const resolvedMinWidth = collapsed ? '0px' : (widthStyle?.minWidth ?? minWidth);
    const resolvedMaxWidth = collapsed ? '0px' : (widthStyle?.maxWidth ?? maxWidth);

    return (
        <div
            ref={setOuterRef}
            data-tile-id={id}
            style={{
                flexShrink: 0,
                height: '100%',
                position: 'sticky',
                right: 0,
                zIndex: STICKY_Z_INDEX,
                marginLeft: gapCancellingMarginLeft,
                maxWidth: resolvedMaxWidth,
                minWidth: resolvedMinWidth,
                transition: `${TILE_WIDTH_CSS_TRANSITION}, margin-left 0.2s ease-out`,
                width: resolvedWidth,
            }}
        >
            <div ref={slotRef} className="h-full flex flex-col" style={{ width: '100%' }}>
                {is.nullOrUndefined(children) ? null : is.nullOrUndefined(ErrorBoundary) ? (
                    children
                ) : (
                    <ErrorBoundary tileId={id}>{children}</ErrorBoundary>
                )}
            </div>
        </div>
    );
};
